import type { DeviceTrackPoints, EstimatedPose, TrackPoint } from './types';

function isFinitePoint(p: TrackPoint): boolean {
  return (
    Number.isFinite(p.lat) &&
    Number.isFinite(p.lng) &&
    Number.isFinite(p.timestampSec)
  );
}

/**
 * Binary search: largest index with timestampSec <= t, or -1.
 */
export function findPointIndexAtOrBefore(
  points: readonly TrackPoint[],
  t: number,
): number {
  let lo = 0;
  let hi = points.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const p = points[mid]!;
    if (p.timestampSec <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u;
}

/**
 * Estimate device position at time t (matches device-api pose estimator:
 * interpolate between surrounding points; no speed gate).
 * Hold last point before t if within holdMaxSec; else absent.
 */
export function estimatePositionAt(
  track: DeviceTrackPoints,
  t: number,
  options: { holdMaxSec: number },
): EstimatedPose | null {
  const points = track.points.filter(isFinitePoint);
  if (points.length === 0) return null;

  const i = findPointIndexAtOrBefore(points, t);
  if (i < 0) {
    return null;
  }

  const left = points[i]!;
  if (left.timestampSec === t) {
    return {
      deviceId: track.deviceId,
      lat: left.lat,
      lng: left.lng,
      timestampSec: t,
    };
  }

  const right = points[i + 1];
  if (right && right.timestampSec > left.timestampSec) {
    const u =
      (t - left.timestampSec) / (right.timestampSec - left.timestampSec);
    if (u >= 0 && u <= 1) {
      return {
        deviceId: track.deviceId,
        lat: lerp(left.lat, right.lat, u),
        lng: lerp(left.lng, right.lng, u),
        timestampSec: t,
      };
    }
  }

  const age = t - left.timestampSec;
  if (age >= 0 && age <= options.holdMaxSec) {
    return {
      deviceId: track.deviceId,
      lat: left.lat,
      lng: left.lng,
      timestampSec: t,
    };
  }

  return null;
}
